import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "../lib/api";
import { Reveal, Overline } from "../components/Reveal";
import ProductCard from "../components/ProductCard";

export default function Brands() {
  const [brands, setBrands] = useState([]);
  useEffect(() => { api.get("/brands").then((r) => setBrands(r.data)).catch(() => {}); }, []);

  const grouped = useMemo(() => {
    const g = {};
    brands.forEach((b) => {
      const letter = b.name[0].toUpperCase();
      (g[letter] = g[letter] || []).push(b);
    });
    return g;
  }, [brands]);
  const letters = Object.keys(grouped).sort();

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="brands-page">
      <Reveal>
        <Overline>The Directory</Overline>
        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3">Brands, A–Z.</h1>
        <p className="mt-5 max-w-xl text-ink/60 text-sm leading-relaxed">
          Brand names are shown as catalog categories. Jigel does not claim authorized-retailer or partnership status with any listed brand.
        </p>
      </Reveal>
      <Reveal className="mt-10 flex flex-wrap gap-2" data-testid="az-nav">
        {letters.map((l) => (
          <a key={l} href={`#letter-${l}`} data-testid={`az-${l}`}
            className="w-9 h-9 border border-border flex items-center justify-center font-mono text-xs hover:bg-ink hover:text-ivory transition-colors">{l}</a>
        ))}
      </Reveal>
      <div className="mt-16 space-y-14">
        {letters.map((l) => (
          <div key={l} id={`letter-${l}`}>
            <p className="font-serif text-5xl text-wine/25">{l}</p>
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
              {grouped[l].map((b) => (
                <Link key={b.slug} to={`/brands/${b.slug}`} data-testid={`brand-list-${b.slug}`}
                  className="group flex items-center justify-between p-6 border-r border-b border-border hover:bg-sand/50 transition-colors">
                  <div>
                    <p className="font-serif text-2xl group-hover:text-wine transition-colors">{b.name}</p>
                    <p className="mt-1 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      {b.product_count} pieces {b.trade_in_eligible ? "· Trade-In eligible" : ""}
                    </p>
                  </div>
                  <ArrowUpRight size={16} strokeWidth={1.5} className="text-muted-foreground group-hover:text-wine group-hover:rotate-45 transition-all" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BrandPage() {
  const { slug } = useParams();
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setBrand(null);
    api.get(`/brands/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(false));
    api.get("/products", { params: { brand: slug } }).then((r) => setProducts(r.data)).catch(() => {});
  }, [slug]);

  if (brand === null) return <div className="py-40 text-center font-serif text-2xl text-muted-foreground">Jigel…</div>;
  if (brand === false) return <div className="py-40 text-center font-serif text-3xl">Brand not found. <Link to="/brands" className="text-wine underline">All brands</Link></div>;

  const newItems = products.filter((p) => !p.is_preloved);
  const preloved = products.filter((p) => p.is_preloved);

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="brand-page">
      <Reveal>
        <Overline>Brand</Overline>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-3">
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter">{brand.name}</h1>
          <p className="font-mono text-sm text-muted-foreground" data-testid="brand-product-count">{brand.product_count} pieces</p>
        </div>
        {brand.description && <p className="mt-5 max-w-xl text-ink/60 leading-relaxed">{brand.description}</p>}
        <div className="mt-6 flex flex-wrap gap-2">
          {(brand.genders || []).map((g) => (
            <Link key={g} to={`/${g}?brand=${brand.slug}`} data-testid={`brand-gender-${g}`}
              className="px-4 py-2 border border-border text-[10px] tracking-[0.25em] uppercase hover:bg-ink hover:text-ivory transition-colors capitalize">{g}</Link>
          ))}
          {brand.trade_in_eligible && (
            <Link to="/trade-in/start" data-testid="brand-tradein-eligible"
              className="px-4 py-2 border border-gold text-gold text-[10px] tracking-[0.25em] uppercase hover:bg-gold hover:text-ink transition-colors">Trade-In Eligible</Link>
          )}
        </div>
      </Reveal>
      {products.length === 0 ? (
        <p className="mt-20 font-serif text-2xl text-muted-foreground">No pieces from {brand.name} right now — check back soon.</p>
      ) : (
        <>
          {newItems.length > 0 && (
            <section className="mt-16">
              <Reveal><h2 className="font-serif text-3xl tracking-tight">New</h2></Reveal>
              <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
                {newItems.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
            </section>
          )}
          {preloved.length > 0 && (
            <section className="mt-20">
              <Reveal><h2 className="font-serif text-3xl tracking-tight">Pre-Loved</h2></Reveal>
              <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12">
                {preloved.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
